import React, { useState } from 'react';
import { observer } from 'mobx-react';
import type { ImageFile } from 'react-dropzone';
import Dropzone from 'react-dropzone';
import { InfoOutlined } from '@material-ui/icons';
import type { ReceiptFileEntry } from '@this/domain/expenses/item';
import ReceiptFileThumbnail from './receipt_file_thumbnail';
import ReceiptLightbox from './receipt_lightbox';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_RECEIPT_FILES = 10;
const ACCEPT_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';

interface Props {
  receiptFilePath?: string;
  receiptFileMimeType?: string;
  receiptFile?: ImageFile;
  existingFiles: ReceiptFileEntry[];
  newFiles: ImageFile[];
  onSelectLegacy: (f: ImageFile) => void;
  onRemoveLegacy: () => void;
  onAddFiles: (files: ImageFile[]) => void;
  onRemoveNewFile: (index: number) => void;
  onRemoveExistingFile: (fileId: number) => void;
  removable?: boolean;
  totalCount: number;
}

type FileSlide = {
  path: string;
  mimeType?: string;
  filename?: string;
  onRemove: () => void;
};

const ReceiptDropzone = observer(
  ({
    receiptFilePath,
    receiptFileMimeType,
    receiptFile,
    existingFiles,
    newFiles,
    onSelectLegacy,
    onRemoveLegacy,
    onAddFiles,
    onRemoveNewFile,
    onRemoveExistingFile,
    removable = true,
    totalCount
  }: Props) => {
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    const [dropError, setDropError] = useState<string | null>(null);

    const handleDrop = (accepted: ImageFile[], rejected: ImageFile[]) => {
      setDropError(null);
      if (rejected.length > 0) {
        if (rejected[0].size > MAX_FILE_SIZE) {
          setDropError('ファイルサイズは20MB以下にしてください');
        } else {
          setDropError('サポートされていないファイル形式です（JPEG・PNG・WebP・PDF）');
        }
        return;
      }
      if (accepted.length === 0) return;

      const remaining = MAX_RECEIPT_FILES - totalCount;
      if (remaining <= 0) {
        setDropError(`添付ファイルは最大${MAX_RECEIPT_FILES}件までです`);
        return;
      }

      const filesToAdd = accepted.slice(0, remaining);
      if (totalCount === 0 && !receiptFilePath && !receiptFile) {
        onSelectLegacy(filesToAdd[0]);
        if (filesToAdd.length > 1) {
          onAddFiles(filesToAdd.slice(1));
        }
      } else {
        onAddFiles(filesToAdd);
      }

      if (accepted.length > remaining) {
        setDropError(`添付ファイルは最大${MAX_RECEIPT_FILES}件までのため、一部のファイルは追加されませんでした`);
      }
    };

    const legacyPath = receiptFile
      ? receiptFile.preview
      : receiptFilePath
      ? `/aiexpense/images/${receiptFilePath}`
      : undefined;

    const slides: FileSlide[] = [];
    if (legacyPath) {
      slides.push({
        path: legacyPath,
        mimeType: receiptFile?.type ?? receiptFileMimeType,
        filename: receiptFile?.name ?? receiptFilePath?.split('/').pop(),
        onRemove: onRemoveLegacy
      });
    }
    existingFiles.forEach(rf => {
      slides.push({
        path: `/aiexpense/images/${rf.filePath}`,
        mimeType: rf.contentType,
        filename: rf.originalFilename,
        onRemove: () => onRemoveExistingFile(rf.id!)
      });
    });
    newFiles.forEach((f, i) => {
      slides.push({
        path: f.preview || '',
        mimeType: f.type,
        filename: f.name,
        onRemove: () => onRemoveNewFile(i)
      });
    });

    const renderThumbnail = (slide: FileSlide, index: number) => (
      <ReceiptFileThumbnail
        key={index}
        path={slide.path}
        mimeType={slide.mimeType}
        filename={slide.filename}
        fallbackLabel={`領収書${index + 1}`}
        onZoom={setLightboxSrc}
        onRemove={removable ? slide.onRemove : undefined}
      />
    );

    return (
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8, color: '#2592c8' }}>
          <InfoOutlined style={{ fontSize: 16 }} />
          <span style={{ fontSize: 13 }}>領収書は最大{MAX_RECEIPT_FILES}件まで添付できます</span>
        </div>
        {slides.length > 0 && (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
            {slides.map((s, i) => renderThumbnail(s, i))}
          </div>
        )}

        {totalCount < MAX_RECEIPT_FILES && (
          <Dropzone
            onDrop={handleDrop}
            accept={ACCEPT_TYPES}
            maxSize={MAX_FILE_SIZE}
            multiple={MAX_RECEIPT_FILES - totalCount > 1}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: 110,
              background: '#f5f9fc',
              border: '1px dashed #ccc',
              margin: '10px 0 20px',
              cursor: 'pointer',
              padding: 20
            }}
          >
            <p>
              ここへ領収書ファイルをドラッグ&amp;ドロップ
              <br />
              もしくは
              <br />
              クリックして領収書ファイルを指定
              <br />
              <span style={{ display: 'block', marginTop: 6, fontSize: 13, color: '#555' }}>
                JPEG・PNG・WebP・PDF（最大20MB）
              </span>
              <span style={{ display: 'block', marginTop: 4, fontSize: 13, color: '#2592c8' }}>
                あと{MAX_RECEIPT_FILES - totalCount}件追加できます
              </span>
            </p>
          </Dropzone>
        )}
        {dropError && <p style={{ fontSize: 12, color: '#c72f62', margin: 0 }}>{dropError}</p>}

        {lightboxSrc && <ReceiptLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      </div>
    );
  }
);

export default ReceiptDropzone;
